// src/components/Employee/Employee.jsx
import React, { useState, useEffect } from 'react';
import employeeService from '../../services/employeeService';
import EmployeeTable from './EmployeeTable';
import EmployeeModal from './EmployeeModal';
import EditEmployeeModal from './EditEmployeeModal';
import { showNotification } from '../../utils/notification';
import '../Inventory/inventory.css';
import { PlusCircle } from 'lucide-react';

const Employee = ({ toggleSidebar }) => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const departmentMap = React.useMemo(() => {
    const map = {};
    departments.forEach(dep => {
      map[dep.id] = dep.name;
    });
    return map;
  }, [departments]);

  const designationMap = React.useMemo(() => {
    const map = {};
    designations.forEach(des => {
      map[des.id] = des.name;
    });
    return map;
  }, [designations]);

  useEffect(() => {
    // Load departments and designations once on component mount
    const loadDropdownData = async () => {
      try {
        const departmentsData = await employeeService.getDepartments();
        console.log('Departments response:', departmentsData);
        setDepartments(departmentsData);
        
        const designationsData = await employeeService.getDesignations();
        setDesignations(designationsData);
      } catch (error) {
        console.error('Dropdown data error:', error);
        showNotification(`Failed to load dropdown data: ${error.message}`, 'error');
      }
    };
    loadDropdownData();
  }, []);

  // Real-time search with debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (page !== 1) {
        setPage(1); // Reset to first page when search changes
      } else {
        loadData(); // If already on page 1, just reload
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Load data when page changes
  useEffect(() => {
    if (page === 1 && searchTerm === '') {
      // Only load if we're on page 1 after initial mount or page change
      loadData();
    } else if (page !== 1) {
      loadData();
    }
  }, [page]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const employeesData = await employeeService.getAll(page, limit, searchTerm);
      setEmployees(employeesData);
      setHasNextPage(employeesData.length === limit);
      
      // Estimate total pages
      if (employeesData.length < limit) {
        setTotalPages(page);
      } else {
        setTotalPages(page + 1);
      }
    } catch (error) {
      showNotification(`Failed to load: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleAddNew = () => {
    setSelectedEmployee(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const handleSave = async (payload) => {
    console.log("========================================");
    console.log("📤 RAW PAYLOAD FROM MODAL:");
    console.log("========================================");
    console.log(JSON.stringify(payload, null, 2));

    const minimalPayload = {
      employeeCode: String(payload.employeeCode).trim(),
      title: String(payload.title || '').trim(),
      firstName: String(payload.firstName).trim(),
      middleName: String(payload.middleName || '').trim(),
      lastName: String(payload.lastName).trim(),
      fullName: String(payload.fullName).trim(),
      
      nic: String(payload.nic).trim(),
      passportNumber: String(payload.passportNumber || '').trim(),
      drivingLicense: String(payload.drivingLicense || '').trim(),
      
      dateOfBirth: payload.dateOfBirth 
        ? new Date(payload.dateOfBirth).toISOString() 
        : null,
      gender: String(payload.gender || '').trim(),
      maritalStatus: String(payload.maritalStatus || '').trim(),
      nationality: String(payload.nationality || 'Sri Lankan').trim(),
      
      personalEmail: String(payload.personalEmail || '').trim(),
      workEmail: String(payload.workEmail || '').trim(),
      contactNumber: String(payload.contactNumber).trim(),
      emergencyContact: String(payload.emergencyContact || '').trim(),
      emergencyContactRelationship: String(payload.emergencyContactRelationship || '').trim(),
      
      address: String(payload.address || '').trim(),
      city: String(payload.city || '').trim(),
      country: String(payload.country || 'Sri Lanka').trim(),
      
      dateJoined: payload.dateJoined 
        ? new Date(payload.dateJoined).toISOString() 
        : null,
      dateConfirmed: payload.dateConfirmed 
        ? new Date(payload.dateConfirmed).toISOString() 
        : null,
      employmentType: String(payload.employmentType || 'PERMANENT').trim(),
      employmentStatus: String(payload.employmentStatus || 'ACTIVE').trim(),
      
      departmentId: parseInt(payload.departmentId, 10),
      designationId: parseInt(payload.designationId, 10),
      basicSalary: parseFloat(payload.basicSalary),
      
      bankName: String(payload.bankName || '').trim(),
      bankBranch: String(payload.bankBranch || '').trim(),
      accountNumber: String(payload.accountNumber || '').trim(),
      accountName: String(payload.accountName || '').trim(),
      
      isActive: Boolean(payload.isActive),
    };

    console.log("========================================");
    console.log("🔧 MINIMAL PAYLOAD (matching Postman):");
    console.log("========================================");
    console.log(JSON.stringify(minimalPayload, null, 2));
    console.log("========================================");

    try {
      setIsSaving(true);
      await employeeService.create(minimalPayload);
      showNotification(`Employee "${minimalPayload.fullName}" created successfully!`, 'success');
      setIsModalOpen(false);
      setPage(1);
      loadData();
    } catch (error) {
      console.error("❌ ERROR:", error.response?.data || error.message);
      
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.message ||
        error.message ||
        'Unknown error occurred';
      
      showNotification(`Failed to create: ${errorMessage}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async (payload) => {
    try {
      setIsSaving(true);
      await employeeService.update(selectedEmployee.id, payload);
      showNotification(`Employee "${payload.fullName}" updated successfully!`, 'success');
      setIsEditModalOpen(false);
      loadData();
    } catch (error) {
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Delete employee ${employee.fullName}?`)) return;
    try {
      await employeeService.delete(employee.id);
      showNotification('Employee deleted successfully', 'success');
      setPage(1);
      loadData();
    } catch (error) {
      showNotification(`Failed to delete: ${error.message}`, 'error');
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <main className="inventory-main-content">
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn">
          ☰
        </button>
        <h2 className="inventory-content-title">Employee Management</h2>
        <div className="header-actions">
          <button className="btn-primary2" onClick={handleAddNew}>
             <PlusCircle size={18} />
            Add New Employee
          </button>
        </div>
      </header>

      <section className="filter-controls">
        <div className="filter-group">
          <input
            type="text"
            className="custom-input search-input"
            placeholder="Search Name/NIC/Code"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button className="btn btn-clear" onClick={handleClearSearch}>
            Clear
          </button>
        </div>
      </section>

      <EmployeeTable
        employees={employees}
        departmentMap={departmentMap}
        designationMap={designationMap}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isLoading={isLoading}
      />

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button
          className="btn-page"
          disabled={page === 1}
          onClick={() => setPage((prev) => prev - 1)}
          type="button"
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {page > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {page > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {page > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page - 1)}
            >
              {page - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{page}</button>
          
          {hasNextPage && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page + 1)}
            >
              {page + 1}
            </button>
          )}
          
          {hasNextPage && page < totalPages - 1 && (
            <>
              {page < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {page} of {totalPages || 1}</span>

        <button
          className="btn-page"
          disabled={!hasNextPage}
          onClick={() => setPage((prev) => prev + 1)}
          type="button"
        >
          Next
        </button>
      </div>

      <EmployeeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isSaving={isSaving}
        departments={departments}
        designations={designations}
      />

      <EditEmployeeModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
        employee={selectedEmployee}
        isSaving={isSaving}
        departments={departments}
        designations={designations}
      />
    </main>
  );
};

export default Employee;